import React, { useState, useEffect } from "react";
import { stripMarkdown } from "../utils/formatAIText";
import {
  TrendingUp,
  Target,
  ArrowRight,
  RotateCw,
  AlertCircle,
  CheckCircle,
  Play,
  Brain,
  RefreshCw,
} from "lucide-react";
import SEO, { PAGE_SEO } from "./SEO";
import { UserProfile, SwingAnalysis } from "../types";
import { aiService } from "../services/aiService";

interface SwingAnalyzerProps {
  userProfile: UserProfile | null;
}

const SwingAnalyzer: React.FC<SwingAnalyzerProps> = ({ userProfile }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [ballFlight, setBallFlight] = useState(
    userProfile?.ballFlight || "straight",
  );
  const [missPattern, setMissPattern] = useState<string[]>([]);
  const [contactIssues, setContactIssues] = useState<string[]>([]);
  const [distanceIssues, setDistanceIssues] = useState<string[]>([]);
  const [analysis, setAnalysis] = useState<SwingAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Load saved analysis from localStorage on mount
  useEffect(() => {
    if (userProfile) {
      const saved = localStorage.getItem(`swingAnalysis_${userProfile.id}`);
      if (saved) {
        try {
          const savedAnalysis = JSON.parse(saved);
          setAnalysis(savedAnalysis);
        } catch (e) {
          console.error('Failed to load saved analysis:', e);
        }
      }
    }
  }, [userProfile]);

  const missPatternsData = [
    "Consistently right", "Consistently left", "Inconsistent direction",
    "Short of target", "Over the target", "Fat shots (hitting ground first)",
    "Thin shots (topping the ball)", "Shanks"
  ];

  const contactProblemsData = [
    "Poor ball striking", "Inconsistent contact", "Loss of distance",
    "High ball flight", "Low ball flight", "Too much spin", "Not enough spin"
  ];

  const distanceProblemsData = [
    "Lack of distance", "Inconsistent distances", "Good contact but short",
    "Loss of distance with age", "Can't reach par 4s in regulation"
  ];

  const questions = [
    {
      id: "ballFlight",
      title: "What's your typical ball flight?",
      subtitle: "Select the option that best describes your shots",
      type: "single" as const,
      options: [
        { value: "slice", label: "Slice", description: "Ball curves left to right (for right-handed)" },
        { value: "fade", label: "Fade", description: "Controlled left to right movement" },
        { value: "straight", label: "Straight", description: "Ball flies relatively straight" },
        { value: "draw", label: "Draw", description: "Controlled right to left movement" },
        { value: "hook", label: "Hook", description: "Ball curves right to left (for right-handed)" },
      ],
    },
    {
      id: "missPatterns",
      title: "Common Miss Patterns",
      subtitle: "Select all that apply to your typical misses",
      type: "multiple" as const,
      options: missPatternsData.map((miss: string) => ({ value: miss, label: miss, description: "" })),
    },
    {
      id: "contactIssues",
      title: "Contact Issues",
      subtitle: "Any problems with ball contact?",
      type: "multiple" as const,
      options: contactProblemsData.map((issue: string) => ({ value: issue, label: issue, description: "" })),
    },
    {
      id: "distanceIssues",
      title: "Distance Issues",
      subtitle: "Any concerns about distance?",
      type: "multiple" as const,
      options: distanceProblemsData.map((issue: string) => ({ value: issue, label: issue, description: "" })),
    },
  ];

  const handleQuestionAnswer = (questionId: string, value: string, type: "single" | "multiple") => {
    if (type === "single") {
      if (questionId === "ballFlight") {
        setBallFlight(value as typeof ballFlight);
      }
    } else if (type === "multiple") {
      const currentAnswers = questionId === "missPatterns" ? missPattern :
                            questionId === "contactIssues" ? contactIssues : distanceIssues;

      const newAnswers = currentAnswers.includes(value)
        ? currentAnswers.filter((item) => item !== value)
        : [...currentAnswers, value];

      if (questionId === "missPatterns") setMissPattern(newAnswers);
      else if (questionId === "contactIssues") setContactIssues(newAnswers);
      else if (questionId === "distanceIssues") setDistanceIssues(newAnswers);
    }
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      analyzeSwing();
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const analyzeSwing = async () => {
    setIsAnalyzing(true);

    try {
      // ALWAYS use AI for intelligent recommendations
      let swingAnalysis: SwingAnalysis;

      if (userProfile) {
        swingAnalysis = await aiService.getAISwingAnalysis({
          userProfile,
          ballFlight,
          missPatterns: missPattern,
          contactIssues,
          goals: distanceIssues,
        });
      } else {
        // Fallback to rule-based if no profile
        swingAnalysis = generateSwingAnalysis(
          ballFlight,
          missPattern,
          contactIssues,
          distanceIssues,
        );
      }

      setAnalysis(swingAnalysis);
      // Save to localStorage for persistence
      if (userProfile) {
        localStorage.setItem(`swingAnalysis_${userProfile.id}`, JSON.stringify(swingAnalysis));
      }
    } catch (error) {
      console.error(
        "AI swing analysis failed, falling back to rule-based:",
        error,
      );

      const swingAnalysis = generateSwingAnalysis(
        ballFlight,
        missPattern,
        contactIssues,
        distanceIssues,
      );
      setAnalysis(swingAnalysis);
      // Save fallback analysis too
      if (userProfile) {
        localStorage.setItem(`swingAnalysis_${userProfile.id}`, JSON.stringify(swingAnalysis));
      }
    }

    setIsAnalyzing(false);
  };

  const startOver = () => {
    setAnalysis(null);
    setCurrentQuestion(0);
    setBallFlight(userProfile?.ballFlight || "straight");
    setMissPattern([]);
    setContactIssues([]);
    setDistanceIssues([]);
    if (userProfile) {
      localStorage.removeItem(`swingAnalysis_${userProfile.id}`);
    }
  };

  const generateSwingAnalysis = (
    flight: string,
    misses: string[],
    contact: string[],
    distance: string[],
  ): SwingAnalysis => {
    const issues: string[] = [];
    const corrections = {
      grip: [] as string[],
      stance: [] as string[],
      swing: [] as string[],
      equipment: [] as string[],
    };
    const drills: { name: string; description: string; frequency: string }[] = [];

    if (flight === "slice") {
      issues.push("Open clubface at impact", "Out-to-in swing path");
      corrections.grip.push(
        "Strengthen grip (rotate hands right)",
        "Check grip pressure - not too tight",
      );
      corrections.stance.push(
        "Align shoulders parallel to target",
        "Check ball position - not too far forward",
      );
      corrections.swing.push(
        "Feel like swinging more from inside",
        "Focus on releasing hands through impact",
      );
      drills.push({
        name: "Towel Drill",
        description: "Place towel under right armpit, swing without dropping it",
        frequency: "10 swings daily",
      });
    } else if (flight === "hook") {
      issues.push("Closed clubface at impact", "In-to-out swing path");
      corrections.grip.push(
        "Weaken grip (rotate hands left)",
        "Maintain light grip pressure",
      );
      corrections.stance.push(
        "Open stance slightly",
        "Ball position slightly back",
      );
      corrections.swing.push(
        "Feel like holding off release",
        "Keep clubface square longer",
      );
    }

    if (misses.includes("Fat shots (hitting ground first)")) {
      issues.push("Weight staying on back foot", "Hitting behind the ball");
      corrections.stance.push("Start with 60% weight on front foot");
      corrections.swing.push("Focus on hitting ball first, then ground");
      drills.push({
        name: "Impact Bag Drill",
        description: "Practice hitting into impact bag to feel proper impact position",
        frequency: "20 swings, 3x per week",
      });
    }

    if (misses.includes("Thin shots (topping the ball)")) {
      issues.push("Coming up out of posture", "Trying to lift the ball");
      corrections.stance.push("Maintain spine angle throughout swing");
      corrections.swing.push("Trust the loft - hit down on the ball");
    }

    if (contact.includes("Loss of distance")) {
      issues.push("Poor energy transfer", "Possible equipment mismatch");
      corrections.equipment.push("Check shaft flex - may be too stiff/soft");
      corrections.swing.push("Focus on solid contact over swing speed");
    }

    if (distance.includes("Lack of distance")) {
      corrections.equipment.push(
        "Consider more lofted driver",
        "Check if clubs fit your swing speed",
      );
      corrections.swing.push(
        "Work on creating lag in downswing",
        "Focus on hip rotation",
      );
      drills.push({
        name: "Hip Turn Drill",
        description: "Practice turning hips while keeping upper body stable",
        frequency: "Daily, 2 minutes",
      });
    }

    drills.push({
      name: "Alignment Stick Drill",
      description: "Use alignment sticks to check setup and swing path",
      frequency: "Before every practice session",
    });

    return {
      ballFlight: `Your typical ball flight is a ${flight}`,
      issues,
      corrections,
      drills,
    };
  };

  const currentQ = questions[currentQuestion];

  return (
    <>
      <SEO {...PAGE_SEO.swingAnalyzer} url="/swing-analyzer" />
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 pb-8">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-8 pt-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600">
                Question {currentQuestion + 1} of {questions.length}
              </span>
              <span className="text-sm font-medium text-gray-600">
                {Math.round(((currentQuestion + 1) / questions.length) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Quiz Card - Hide when results are shown */}
          {!analysis && (
            <div className="card p-8 mb-8">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
                  <TrendingUp size={32} className="text-orange-600" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Swing Analyzer</h1>
                <p className="text-gray-600 text-lg">AI-powered swing analysis and improvement suggestions</p>
              </div>

            {/* Question */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{currentQ.title}</h2>
              <p className="text-gray-600">{currentQ.subtitle}</p>
            </div>

            {/* Options */}
            <div className="space-y-3 mb-8">
              {currentQ.options.map((option, index) => {
                const isSelected = currentQ.type === "single"
                  ? (currentQ.id === "ballFlight" ? ballFlight === option.value : false)
                  : (currentQ.id === "missPatterns" ? missPattern.includes(option.value) :
                     currentQ.id === "contactIssues" ? contactIssues.includes(option.value) :
                     distanceIssues.includes(option.value));

                return (
                  <button
                    key={option.value}
                    onClick={() => handleQuestionAnswer(currentQ.id, option.value, currentQ.type)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? "border-orange-300 bg-orange-50 text-orange-900"
                        : "border-gray-200 bg-white hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 mb-1">
                          {option.label}
                        </div>
                        {option.description && (
                          <div className="text-sm text-gray-600">
                            {option.description}
                          </div>
                        )}
                      </div>
                      {isSelected && (
                        <CheckCircle size={20} className="text-orange-600 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-6 border-t border-gray-200">
              <button
                onClick={prevQuestion}
                disabled={currentQuestion === 0}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  currentQuestion === 0
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <ArrowRight size={16} className="transform rotate-180" />
                <span>Previous</span>
              </button>

              <button
                onClick={nextQuestion}
                disabled={isAnalyzing}
                className={`flex items-center space-x-2 px-6 py-2 rounded-lg transition-colors ${
                  isAnalyzing
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-orange-600 text-white hover:bg-orange-700"
                }`}
              >
                {isAnalyzing ? (
                  <>
                    <TrendingUp size={16} className="animate-pulse" />
                    <span>Analyzing...</span>
                  </>
                ) : currentQuestion === questions.length - 1 ? (
                  <>
                    <Brain size={16} />
                    <span>AI Analyze Swing</span>
                  </>
                ) : (
                  <>
                    <span>Next</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
          )}

          {/* Analysis Results - Replace quiz when complete */}
          {analysis && (
            <div className="mt-8">
              <div className="card">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        Swing Analysis Results
                      </h2>
                      <p className="text-gray-600">{analysis.ballFlight}</p>
                    </div>
                    <button
                      onClick={startOver}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-lg hover:from-orange-700 hover:to-amber-700 transition-all shadow-md text-sm font-semibold"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span>Start Over</span>
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Identified Issues */}
                  {analysis.issues.length > 0 && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center space-x-2">
                        <AlertCircle size={18} className="text-red-500" />
                        <span>Identified Issues</span>
                      </h3>
                      <div className="space-y-2">
                        {analysis.issues.map((issue, index) => (
                          <div
                            key={index}
                            className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg"
                          >
                            <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                            <p className="text-sm text-red-700">{stripMarkdown(issue)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Corrections */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Recommended Corrections
                    </h3>

                    {Object.entries(analysis.corrections).map(
                      ([category, corrections]) =>
                        corrections.length > 0 && (
                          <div key={category} className="mb-4">
                            <h4 className="font-medium text-gray-800 mb-2 capitalize flex items-center space-x-2">
                              {category === "grip" && <Target size={16} />}
                              {category === "stance" && (
                                <ArrowRight size={16} />
                              )}
                              {category === "swing" && <RotateCw size={16} />}
                              {category === "equipment" && (
                                <TrendingUp size={16} />
                              )}
                              <span>{category} Adjustments</span>
                            </h4>
                            <div className="space-y-2 ml-6">
                              {corrections.map((correction, index) => (
                                <div
                                  key={index}
                                  className="flex items-start space-x-3"
                                >
                                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2"></div>
                                  <p className="text-sm text-gray-600">
                                    {stripMarkdown(correction)}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ),
                    )}
                  </div>

                  {/* Practice Drills */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center space-x-2">
                      <Play size={18} className="text-green-500" />
                      <span>Practice Drills</span>
                    </h3>
                    <div className="space-y-4">
                      {analysis.drills.map((drill, index) => (
                        <div
                          key={index}
                          className="p-4 bg-green-50 rounded-lg"
                        >
                          <h4 className="font-medium text-green-900 mb-2">
                            {stripMarkdown(drill.name)}
                          </h4>
                          <p className="text-sm text-green-700 mb-2">
                            {stripMarkdown(drill.description)}
                          </p>
                          <div className="text-xs text-green-600 font-medium">
                            Frequency: {drill.frequency}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Next Steps */}
                  <div className="bg-blue-50 rounded-lg p-4 mb-6">
                    <h3 className="font-medium text-blue-900 mb-2">
                      Next Steps
                    </h3>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Practice the recommended drills consistently</li>
                      <li>• Consider a lesson with a PGA professional</li>
                      <li>• Record your swing to monitor progress</li>
                      <li>• Be patient - swing changes take time to develop</li>
                    </ul>
                  </div>

                  {/* Analyze Again Button */}
                  <div className="flex justify-center pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        setAnalysis(null);
                        setCurrentQuestion(0);
                        setBallFlight(userProfile?.ballFlight || "straight");
                        setMissPattern([]);
                        setContactIssues([]);
                        setDistanceIssues([]);
                      }}
                      className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-semibold px-8 py-3 rounded-xl transition-all shadow-lg hover:shadow-xl flex items-center space-x-2"
                    >
                      <RotateCw size={18} />
                      <span>Analyze Again</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default SwingAnalyzer;